import { useTranslation } from "react-i18next"
import { ChevronLeftIcon } from "lucide-react"

import { useAgentState } from "@/agent-state"
import { DelayedClickButton } from "@/components/DelayedButton"
import Icon from "@/components/Icon"
import { SingleClickButton } from "@/components/SingleClickButton"
import { Button } from "@/components/ui/button"
import { TooltipWrapper } from "@/components/ui/tooltip"
import { isNull } from "@/helpers/typeguards"
import { CaseWithActivityFeed } from "@/pages/Campaigns/student-details/student-details-api"

import { useACWButtonsHook } from "./ACWButtons/hook"
import { useCallPanelActions } from "./callpanel-actions"
import { CallPanelConfirmButton } from "./callpanel-confirm-button"
import { useGetEnabledCallConditions } from "./ManualCallButtons/hook"

type Props = {
  actions: ReturnType<typeof useCallPanelActions>
  acwButtonState: {
    isDisabled: boolean
    isManualCall?: boolean
  } & ReturnType<typeof useACWButtonsHook>
  campaignName: string | null
  caseData: CaseWithActivityFeed | null
}

export function CallPanelButtons({
  actions,
  acwButtonState,
  campaignName,
  caseData,
}: Props) {
  const {
    acceptInboundCall,
    addStatusInModal,
    backToCallMode,
    call,
    endCall,
    endTaskError,
    isMuted,
    isOnHold,
    manualCall,
    rejectInboundCall,
    skip,
    toggleHold,
    toggleMute,
  } = actions

  const { state } = useAgentState()

  if (state.matches("offline")) {
    if (isNull(campaignName) || isNull(caseData)) return null

    return (
      <ManualCallIdleButtons
        campaignName={campaignName}
        caseData={caseData}
        onAddStatus={addStatusInModal}
        onCall={manualCall}
      />
    )
  }

  if (
    state.matches({ busy: { pre_call: "normal" } }) &&
    state.context.displayStatus === "online"
  ) {
    return <ConnectedTaskButtons onCall={call} onSkipCall={skip} />
  }

  if (state.matches({ busy: { pre_call: "no_call" } })) {
    return (
      <SkipCallModeButtons
        isLoading={acwButtonState.isLoading}
        isDisabled={acwButtonState.isDisabled}
        onConfirmAndGoNext={async () => {
          await acwButtonState.onConfirmAndGoNext(false)
        }}
        onConfirmAndGoOffline={async () => {
          await acwButtonState.onConfirmAndGoOffline(false)
        }}
        onGoBack={backToCallMode}
      />
    )
  }

  if (state.matches({ busy: "outbound_ringing" })) {
    return <OutboundCallRingingButtons onHangUp={endCall} />
  }

  if (state.matches({ busy: "inbound_ringing" })) {
    return (
      <InboundCallRingingButtons
        onAccept={acceptInboundCall}
        onReject={rejectInboundCall}
      />
    )
  }

  if (state.matches({ busy: "in_call" })) {
    return (
      <InCallButtons
        isMuted={isMuted}
        isOnHold={isOnHold}
        onEndCall={endCall}
        onToggleHold={toggleHold}
        onToggleMute={toggleMute}
      />
    )
  }

  if (state.matches({ busy: "after_call" })) {
    return <ACWButtons {...acwButtonState} />
  }

  if (state.matches({ busy: "error" })) {
    return (
      <CallErrorButtons
        onEndTaskError={endTaskError}
        isNetworkError={state.matches({
          busy: { error: "missed_call_agent_state" },
        })}
      />
    )
  }

  return null
}

export function InboundCallRingingButtons({
  onAccept,
  onReject,
}: {
  onAccept: () => Promise<void>
  onReject: () => Promise<void>
}) {
  const { t } = useTranslation()

  return (
    <>
      <SingleClickButton variant="destructive" type="button" onClick={onReject}>
        <Icon name="end-call" />
        {t("global.button.skip")}
      </SingleClickButton>
      <SingleClickButton variant="success" type="button" onClick={onAccept}>
        <Icon name="accept-call" />
        {t("global.button.acceptInbound")}
      </SingleClickButton>
    </>
  )
}

export function OutboundCallRingingButtons({
  onHangUp,
}: {
  onHangUp: () => Promise<void>
}) {
  const { t } = useTranslation()

  return (
    <SingleClickButton variant="destructive" type="button" onClick={onHangUp}>
      <Icon name="end-call" />
      {t("global.button.endCall")}
    </SingleClickButton>
  )
}

export function InCallButtons({
  isMuted,
  isOnHold,
  onEndCall,
  onToggleHold,
  onToggleMute,
}: {
  isMuted: boolean
  isOnHold: boolean
  onEndCall: () => Promise<void>
  onToggleHold: () => Promise<void>
  onToggleMute: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { state } = useAgentState()
  const isLostConnection = state.context.networkingErrors.includes(
    "web_socket_connection_lost",
  )

  return (
    <>
      <DelayedClickButton
        type="button"
        variant="outline"
        className="w-[126px]"
        onClick={onToggleHold}
        isDisabled={isLostConnection}
      >
        <Icon name={isOnHold ? "resume-call" : "hold-call"} />
        {t(`global.button.${isOnHold ? "resume" : "hold"}`)}
      </DelayedClickButton>
      <Button
        type="button"
        variant="outline"
        className="w-[156px]"
        onClick={onToggleMute}
        isDisabled={isLostConnection}
      >
        <Icon name={isMuted ? "unmute-call" : "mute-call"} />
        {t(`global.button.${isMuted ? "unmute" : "mute"}`)}
      </Button>
      <SingleClickButton
        variant="destructive"
        type="button"
        onClick={onEndCall}
        isDisabled={isLostConnection}
      >
        <Icon name="end-call" />
        {t("global.button.endCall")}
      </SingleClickButton>
    </>
  )
}

export function ConnectedTaskButtons({
  onCall,
  onSkipCall,
}: {
  onCall: () => Promise<void>
  onSkipCall: () => void
}) {
  const { t } = useTranslation()

  return (
    <>
      <SingleClickButton type="button" variant="success" onClick={onCall}>
        <Icon name="accept-call" />
        {t("global.button.call")}
      </SingleClickButton>
      <Button variant="outline" type="button" onClick={onSkipCall}>
        <Icon name="end-call" />
        {t("global.button.wontCall")}
      </Button>
    </>
  )
}

export function SkipCallModeButtons({
  isDisabled,
  isLoading,
  onConfirmAndGoNext,
  onConfirmAndGoOffline,
  onGoBack,
}: {
  isDisabled: boolean
  isLoading: boolean
  onConfirmAndGoNext: () => Promise<void>
  onConfirmAndGoOffline: () => Promise<void>
  onGoBack: () => Promise<void>
}) {
  const { t } = useTranslation()

  return (
    <>
      <Button variant="outline" onClick={onGoBack}>
        <ChevronLeftIcon />
        {t("global.button.goBack")}
      </Button>
      <CallPanelConfirmButton
        onConfirmAndGoNext={onConfirmAndGoNext}
        onConfirmAndGoOffline={onConfirmAndGoOffline}
        isDisabled={isDisabled}
        isLoading={isLoading}
      />
    </>
  )
}

export function ACWButtons({
  isDisabled,
  isLoading,
  isManualCall = false,
  onConfirmAndGoNext,
  onConfirmAndGoOffline,
}: {
  isDisabled: boolean
  isManualCall?: boolean
} & ReturnType<typeof useACWButtonsHook>) {
  const { t } = useTranslation()

  if (isManualCall) {
    return (
      <Button
        type="button"
        onClick={async () => onConfirmAndGoNext(true)}
        isDisabled={isDisabled}
        isLoading={isLoading}
      >
        {t("global.button.endTask")}
      </Button>
    )
  }

  return (
    <CallPanelConfirmButton
      onConfirmAndGoNext={() => onConfirmAndGoNext(true)}
      onConfirmAndGoOffline={() => onConfirmAndGoOffline(true)}
      isDisabled={isDisabled}
      isLoading={isLoading}
    />
  )
}

export function CallErrorButtons({
  isNetworkError,
  onEndTaskError,
}: {
  isNetworkError: boolean
  onEndTaskError: () => Promise<void>
}) {
  const { t } = useTranslation()

  return (
    <Button type="button" onClick={onEndTaskError}>
      {isNetworkError ? t("global.button.close") : t("global.button.endTask")}
    </Button>
  )
}

function ManualCallIdleButtons({
  campaignName,
  caseData,
  onAddStatus,
  onCall,
}: {
  campaignName: string
  caseData: CaseWithActivityFeed
  onAddStatus: (caseData: CaseWithActivityFeed) => Promise<void>
  onCall: (queueARN: string, caseData: CaseWithActivityFeed) => Promise<void>
}) {
  const { t } = useTranslation()
  const { fetchCampaignQuery, fetchQueueARNQuery } =
    useGetEnabledCallConditions(campaignName)
  const queueARN = fetchQueueARNQuery?.data?.data.queue_arn
  const campaignEnabled = fetchCampaignQuery?.data?.data.enabled || false
  const isLoading =
    fetchQueueARNQuery?.isLoading && fetchCampaignQuery?.isLoading

  const handleCall = async () => {
    if (!queueARN) return
    await onCall(queueARN, caseData)
  }
  const enableCall = campaignEnabled && queueARN
  const tooltip = campaignEnabled
    ? queueARN
      ? undefined
      : t("callPanel.reason.invalidQueueARN")
    : t("callPanel.reason.disabledCampaign")

  return (
    <>
      <Button onClick={() => onAddStatus(caseData)} variant="outline">
        {t("global.button.addStatus")}
      </Button>
      <TooltipWrapper tooltip={tooltip}>
        <SingleClickButton
          isLoading={isLoading}
          isDisabled={!enableCall}
          onClick={handleCall}
        >
          {t("global.button.call")}
        </SingleClickButton>
      </TooltipWrapper>
    </>
  )
}
